/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-magic-numbers,spellcheck/spell-checker */
import { addMinutes } from 'date-fns';

import {
    AppPhase,
    DealerAssistTableErrorCode,
    GameType,
    NotificationCode,
    NotificationSeverity,
    SupervisorAction,
    SupervisorSubPhase,
    TableMode,
    WorkerAccessLevel
} from '../models/communication/app-enums.model';
import { AppEventType } from '../models/communication/app-events.model';
import { AppState, Game } from '../models/communication/app-state.model';
import { YEventPayload } from '../models/y-protocol.model';

type Event = Omit<YEventPayload<AppEventType>, 'triggered'>;

export const games: Game[] = [
    { id: 0, gameType: GameType.Baccarat, displayName: 'Baccarat', isVirtual: false },
    { id: 1, gameType: GameType.DragonTiger, displayName: 'Dragon Tiger', isVirtual: false },
    { id: 2, gameType: GameType.OpenBaccarat, displayName: 'Open Baccarat', isVirtual: false },
    { id: 3, gameType: GameType.OpenDragonTiger, displayName: 'Open Dragon Tiger', isVirtual: false },
    { id: 4, gameType: GameType.ThreeHeadedDragon, displayName: 'Three Headed Dragon', isVirtual: false },

    { id: 5, gameType: GameType.Baccarat, displayName: 'Virtual Baccarat', isVirtual: true },
    { id: 6, gameType: GameType.DragonTiger, displayName: 'Virtual Dragon Tiger', isVirtual: true },
    { id: 7, gameType: GameType.OpenBaccarat, displayName: 'Virtual Open Baccarat', isVirtual: true },
    { id: 8, gameType: GameType.OpenDragonTiger, displayName: 'Virtual Open Dragon Tiger', isVirtual: true },
    { id: 9, gameType: GameType.ThreeHeadedDragon, displayName: 'Virtual Three Headed Dragon', isVirtual: true }
];

const supervisorActions = [
    SupervisorAction.VoidGame,
    SupervisorAction.ChangeGame,
    SupervisorAction.ChangeResult,
    SupervisorAction.CloseTable,
    SupervisorAction.ContinueGame,
    SupervisorAction.ChangeShoe,
    SupervisorAction.FreeHand
];

// The game that will trigger the game-specific demo events.
// The Baccarat demo events contain additional (not only CardDealt) events
// that demonstrate different game states during the game.
const game = games[0];

// The tableMode that will be used in demo (LandBased or Online).
const tableMode = TableMode.Online;

export const configuration = {
    appVersion: '1.0.0-demo',
    tableId: 'tronius-table-1',
    decisionTimeInSeconds: 5,
    bettingWindowInSeconds: 3,
    // eslint-disable-next-line spellcheck/spell-checker
    videoStreamId: '2e23782e-8151-4b7f-8ccd-2d9771aa9924',
    supervisorActions,
    game,
    tableMode
};

const chatMessages = [
    {
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        from: 'Billy Bob',
        timestamp: addMinutes(Date.now(), -3).getTime()
    },
    {
        content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis.',
        from: 'Supervisor',
        timestamp: addMinutes(Date.now(), -4).getTime(),
        isSupervisor: true
    },
    {
        content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
        from: 'John Doe',
        timestamp: addMinutes(Date.now(), -5).getTime()
    },
    {
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        from: 'Billy Bob',
        timestamp: addMinutes(Date.now(), -6).getTime()
    },
    {
        content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis risus eget urna mollis ornare vel eu leo.',
        from: 'Supervisor',
        timestamp: addMinutes(Date.now(), -7).getTime(),
        isSupervisor: true
    },
    {
        content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
        from: 'John Doe',
        timestamp: addMinutes(Date.now(), -8).getTime()
    },
    {
        content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        from: 'Billy Bob',
        timestamp: addMinutes(Date.now(), -9).getTime()
    },
    {
        content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis risus eget urna mollis ornare vel eu leo.',
        from: 'Supervisor',
        timestamp: addMinutes(Date.now(), -10).getTime(),
        isSupervisor: true
    },
    {
        content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
        from: 'John Doe',
        timestamp: addMinutes(Date.now(), -11).getTime()
    }
];

/**
 * Initial state that contains configurable {@link game} and {@link tableMode} in `state.configuration`.
 */
export const getDemoInitialState = (gameConfig: Partial<AppState['configuration']> = {}): AppState => ({
    configuration: {
        ...configuration,
        ...gameConfig
    },

    roundState: {
        phase: AppPhase.TableClosed
    },

    flagsState: {
        chatEnabled: true
    },

    chatMessages
});

/**
 * Initial events shared between all the game types.
 */
export const demoEvents: Array<Event | Event[]> = [
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.TableClosed
        }
    },
    [
        {
            name: AppEventType.SupervisorStateChange,
            data: {
                subPhase: SupervisorSubPhase.GameSelection
            }
        },
        {
            name: AppEventType.LoginChange,
            data: {
                new: {
                    accessLevel: WorkerAccessLevel.Supervisor,
                    id: 'BBBBBBBB',
                    location: 'Ljubljana Office',
                    name: 'Tronius Supervisor'
                },
                old: {
                    accessLevel: WorkerAccessLevel.None,
                    id: '',
                    location: '',
                    name: ''
                }
            }
        }
    ],
    {
        name: AppEventType.SupervisorStateChange,
        data: {
            selectedGame: game,
            subPhase: SupervisorSubPhase.ConfirmGameSelection
        }
    },
    [
        {
            name: AppEventType.GameStateChange,
            data: {
                state: {
                    chatMessages,
                    configuration,
                    flagsState: {
                        chatEnabled: true,
                        gameChangingRequested: false,
                        resultChangingRequested: false,
                        roundFlagged: false,
                        supervisorCalled: false,
                        tableClosingRequested: false
                    },
                    playerCount: 0,
                    roundState: {
                        phase: 'WaitingForCroupier'
                    },
                    supervisorState: {},
                    tableState: {}
                }
            }
        },
        {
            name: AppEventType.LoginChange,
            data: {
                new: {
                    accessLevel: WorkerAccessLevel.None,
                    id: '',
                    location: '',
                    name: ''
                },
                old: {
                    accessLevel: WorkerAccessLevel.Supervisor,
                    id: 'BBBBBBBB',
                    location: 'Ljubljana',
                    name: 'Tronius Supervisor'
                }
            }
        },
        {
            name: AppEventType.GamePhaseChange,
            data: {
                phase: AppPhase.WaitingForCroupier
            }
        }
    ],

    [
        {
            name: AppEventType.LoginChange,
            data: {
                new: {
                    accessLevel: WorkerAccessLevel.Croupier,
                    id: 'FFFF0000',
                    location: 'Ljubljana',
                    name: 'Tronius Croupier'
                },
                old: {
                    accessLevel: WorkerAccessLevel.None,
                    id: '',
                    location: '',
                    name: ''
                }
            }
        },
        {
            name: AppEventType.GamePhaseChange,
            data: {
                phase: AppPhase.WaitingForPlayers
            }
        }
    ],

    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.RoundError,
            errorCode: DealerAssistTableErrorCode.BackendDisconnected
        }
    },
    {
        name: AppEventType.Notification,
        data: {
            code: NotificationCode.ScannerDisconnected,
            severity: NotificationSeverity.Warning
        }
    },
    {
        name: AppEventType.Notification,
        data: {
            code: NotificationCode.ScannerConnected,
            severity: NotificationSeverity.Info
        }
    },
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.ShoeChange
        }
    },
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.CardBurn
        }
    },
    {
        name: AppEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 0,
            totalToBurnCount: 7
        }
    },
    {
        name: AppEventType.ChatMessage,
        data: {
            content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam id dolor id nibh ultricies vehicula',
            from: 'Dick Tracy',
            timestamp: Date.now()
        }
    },
    {
        name: AppEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 1,
            totalToBurnCount: 7
        }
    },
    {
        name: AppEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 2,
            totalToBurnCount: 7
        }
    },
    {
        name: AppEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 7,
            totalToBurnCount: 7
        }
    },
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.NewRoundPreparation,
            roundId: 3 + Math.round(Math.random() * 100)
        }
    },
    {
        name: AppEventType.PlayerCountChange,
        data: {
            count: 10 + Math.round(Math.random() * 100)
        }
    },
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.BetsOpen
        }
    },
    {
        name: AppEventType.GamePhaseChange,
        data: {
            phase: AppPhase.BetsClosed
        }
    }
];
